/**
 * Image Upload Component
 * Upload diagrams, screenshots, handwriting for AI analysis
 */

import React, { useState, useRef } from 'react';
import './ImageUpload.css';

const ImageUpload = ({ onImageUpload, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Supported file types
  const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPEG, or WEBP images.');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File too large. Maximum size is 10MB.');
      return false;
    }

    return true;
  };

  const processFile = async (file) => {
    if (!validateFile(file)) {
      return;
    }

    setError(null);
    setIsAnalyzing(true);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Convert to base64 for API
    const base64 = await fileToBase64(file);

    // Send to parent component
    const imageData = {
      file,
      base64,
      preview: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
      size: file.size
    };

    try {
      await onImageUpload(imageData);
      setIsAnalyzing(false);
    } catch (error) {
      setError('Failed to analyze image. Please try again.');
      setIsAnalyzing(false);
      setPreview(null);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleClearPreview = () => {
    setPreview(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          processFile(file);
          e.preventDefault();
        }
      }
    }
  };

  // Add paste listener
  React.useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, []);

  return (
    <div className="image-upload-container">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {!preview ? (
        <button
          className="image-upload-button"
          onClick={handleButtonClick}
          disabled={disabled}
          title="Upload image (diagram, screenshot, or handwriting)"
        >
          <span className="upload-icon">📷</span>
        </button>
      ) : (
        <div className="image-preview-container">
          <div className="image-preview">
            <img src={preview} alt="Uploaded preview" />
            
            {isAnalyzing && (
              <div className="analyzing-overlay">
                <div className="analyzing-spinner"></div>
                <p>Analyzing image...</p>
              </div>
            )}
            
            <button
              className="clear-preview"
              onClick={handleClearPreview}
              title="Remove image"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Drag & Drop Zone (appears on drag) */}
      {isDragging && (
        <div
          ref={dropZoneRef}
          className="drag-drop-overlay"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="drop-zone">
            <span className="drop-icon">📷</span>
            <p>Drop image here</p>
            <span className="drop-hint">PNG, JPEG, WEBP (max 10MB)</span>
          </div>
        </div>
      )}

      {error && (
        <div className="upload-error-toast">
          {error}
        </div>
      )}

      {/* Helper Text */}
      <div className="upload-hints">
        <span className="hint-item" title="Click camera icon to upload">📷 Upload</span>
        <span className="hint-item" title="Drag & drop images">🖱️ Drag</span>
        <span className="hint-item" title="Paste from clipboard (Ctrl+V)">📋 Paste</span>
      </div>
    </div>
  );
};

export default ImageUpload;
